import { useNavigate } from "react-router";
import { type CSSProperties } from "react";
import { useMcpEndpoints } from "../hooks/use-mcp-endpoints";
import { useToolCountForWorkspace } from "../hooks/use-tool-count";
import { useChatScope } from "../hooks/use-chat-scope";
import { EndpointRow } from "../components/endpoints/EndpointRow";
import { showToast } from "../components/primitives";

/**
 * Sprint 17 — Settings → MCP Endpoints. Lists every endpoint the workspace
 * exposes (native scope routes + one per federated connection), each with a
 * copy-URL button, tool count, scope badge, and last-used hint. Backed by
 * `list_mcp_endpoints` via {@link useMcpEndpoints}.
 */
export function EndpointsSettingsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMcpEndpoints();
  // Sprint 16f (S16F.14) — recommend scoped attachment when the workspace tool
  // count exceeds what most hosts can ingest in one tools/list.
  const scale = useToolCountForWorkspace();
  // Sprint 23.9 (Fix 6 / S23.9.22) — explicit workspace scope (was a fall-through).
  useChatScope({ kind: "workspace", label: "Endpoints" }, null, []);

  async function copyUrl(url: string, path: string) {
    try {
      await navigator.clipboard.writeText(url);
      showToast(`Copied ${path} URL`);
    } catch {
      showToast("Couldn't copy to clipboard", "error", true);
    }
  }

  // Sprint 24.11 — per-source endpoints are the connection (`/mcp/m/`, canonical;
  // `/mcp/c/` legacy alias) and wrapped-API (`/mcp/w/`) routes; everything else is
  // a native scope route. The `/mcp/m/` prefix was missing here before 24.11, so
  // connection endpoints leaked into the native list.
  const isPerSource = (p: string) =>
    p.startsWith("/mcp/m/") || p.startsWith("/mcp/c/") || p.startsWith("/mcp/w/");
  const all = data?.endpoints ?? [];
  const native = all.filter((e) => !isPerSource(e.path));
  const perSource = all.filter((e) => isPerSource(e.path));

  return (
    <div style={container}>
      <h1 style={{ fontSize: 22, fontWeight: 500, marginBottom: 8 }}>MCP endpoints</h1>
      <p style={{ fontSize: 14, color: "var(--color-text-secondary)", marginBottom: 24, lineHeight: 1.5 }}>
        Kuvio serves one backend through several routes, each exposing a filtered slice of the tool list.
        Attach the right one to each host — the full surface to Cowork, the lite subset to Cursor, the admin
        surface to a configuration chat. Copy a URL below, or use{" "}
        <kbd style={kbd}>⌘K</kbd> → “Copy MCP endpoint URL”.
      </p>

      {scale.exceedsThreshold && (
        <div style={banner}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <i className="ti ti-alert-triangle" style={{ fontSize: 15 }} aria-hidden="true" />
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              This workspace has {scale.totalToolCount} tools
            </span>
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.5, margin: "0 0 10px" }}>
            Most hosts can’t ingest all of them in one tool list
            {scale.umbrellaAttached ? " — and this session is attached to the umbrella " : ", so attach "}
            {scale.umbrellaAttached ? <code style={codeInline}>/mcp</code> : null}
            {scale.umbrellaAttached ? ". Attach " : null}
            <code style={codeInline}>/mcp/lite</code> plus your credentialled wrappers below for full
            coverage instead of the umbrella <code style={codeInline}>/mcp</code>.
          </p>
          {scale.plan.recommended.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {scale.plan.recommended.map((ep) => (
                <button
                  key={ep.path}
                  onClick={() => copyUrl(ep.url, ep.path)}
                  style={bannerCopyBtn}
                  title={`Copy ${ep.url}`}
                >
                  <i className="ti ti-copy" style={{ fontSize: 13 }} aria-hidden="true" />
                  {ep.path}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isLoading && <div style={muted}>Loading endpoints…</div>}
      {isError && (
        <div style={{ ...muted, color: "var(--c-red-600, var(--color-text-secondary))" }}>
          Couldn't load endpoints. Make sure you're connected to an MCP session.
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
            {native.map((ep) => (
              <EndpointRow key={ep.path} endpoint={ep} />
            ))}
          </div>

          <h2 style={sectionHeading}>Per-source endpoints</h2>
          {perSource.length > 0 ? (
            <>
              <p style={perSourceNote}>
                <i className="ti ti-lock" style={{ fontSize: 13 }} aria-hidden="true" />
                Per-tenant URLs — auth-bound to this workspace. Two workspaces have different URLs even
                for the same connector, so these aren’t shareable.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 28 }}>
                {perSource.map((ep) => (
                  <EndpointRow key={ep.path} endpoint={ep} />
                ))}
              </div>
            </>
          ) : (
            <div style={{ ...emptyHint, marginBottom: 28 }}>
              Per-source endpoints appear here when you add an MCP connection (Sprint 7.0) or a
              wrapped API (Sprint 16). Each gets its own isolated, per-tenant{" "}
              <code style={codeInline}>/mcp/m/&lt;handle&gt;</code> or{" "}
              <code style={codeInline}>/mcp/w/&lt;handle&gt;</code> URL, where the handle is{" "}
              <code style={codeInline}>&lt;slug&gt;_&lt;id&gt;</code> (e.g.{" "}
              <code style={codeInline}>zendesk_a8f3e2</code>).
            </div>
          )}
        </>
      )}

      <button onClick={() => navigate(-1)} style={{ fontSize: 13 }}>
        <i className="ti ti-arrow-left" style={{ fontSize: 14, marginRight: 6 }} aria-hidden="true" />
        Back
      </button>
    </div>
  );
}

const container: CSSProperties = {
  maxWidth: 680,
  margin: "0 auto",
  padding: "48px 0",
  fontFamily: "var(--font-sans)",
};

const sectionHeading: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: 12,
};

const muted: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  padding: "16px 0",
};

const emptyHint: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
  padding: "16px 18px",
  border: "1px dashed var(--color-border-default, var(--color-text-tertiary))",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-secondary)",
};

const perSourceNote: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12.5,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
  margin: "0 0 12px",
};

const codeInline: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 12,
};

const banner: CSSProperties = {
  marginBottom: 24,
  padding: "14px 16px",
  borderRadius: "var(--border-radius-md)",
  border: "1px solid var(--c-amber-300, var(--color-border-default, var(--color-text-tertiary)))",
  background: "var(--c-amber-50, var(--color-background-secondary))",
  color: "var(--c-amber-900, var(--color-text-primary))",
};

const bannerCopyBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  fontFamily: "var(--font-mono, var(--font-sans))",
  padding: "5px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  cursor: "pointer",
};

const kbd: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 11,
  padding: "1px 5px",
  border: "var(--kuvio-border-default)",
  borderRadius: 4,
  background: "var(--color-background-secondary)",
};
